/**
 * SEMH Strategy Catalog with Eligibility Rules and Fallbacks
 * Defines complete strategy specifications including prerequisites and automatic fallbacks
 * when strategies don't match a child's communication/cognitive abilities.
 */

/**
 * Strategy specification object
 * @typedef {Object} StrategySpec
 * @property {string} slug - Unique identifier
 * @property {string} title - Display name
 * @property {'ongoing'|'structured'} category - Strategy type
 * @property {number} defaultFrequency - Default sessions per week
 * @property {number} defaultDurationMins - Default session duration
 * @property {Function} isEligible - Eligibility check function
 * @property {string[]} fallbacks - Fallback strategies if ineligible
 * @property {string} rationale - Why this strategy is recommended
 */

/**
 * Check if Break Card strategy is eligible
 * @param {Object} profile - Child's ability profile
 * @returns {boolean} True if eligible
 */
function isBreakCardEligible(profile) {
  return profile.canUseSymbolicLanguage || 
         (['A', 'B', 'C'].includes(profile.receptiveTier) && profile.expressiveTier !== 'E');
}

/**
 * Check if Restorative Conversation strategy is eligible
 * @param {Object} profile - Child's ability profile
 * @returns {boolean} True if eligible
 */
function isRestorativeConversationEligible(profile) {
  return profile.canProcessSimpleReflection;
}

/**
 * Check if Token/Reward Systems strategy is eligible
 * @param {Object} profile - Child's ability profile
 * @returns {boolean} True if eligible
 */
function isTokenSystemsEligible(profile) {
  return profile.canUseTokenSystems;
}

/**
 * Check if Mindfulness/Breathing strategy is eligible
 * @param {Object} profile - Child's ability profile
 * @returns {boolean} True if eligible
 */
function isMindfulnessEligible(profile) {
  return profile.canImitateActions;
}

/**
 * Check if Social Skills Games strategy is eligible
 * @param {Object} profile - Child's ability profile
 * @returns {boolean} True if eligible
 */
function isSocialSkillsGamesEligible(profile) {
  return profile.canTolerateSmallGroups;
}

/**
 * Check if Check-in/Check-out strategy is eligible
 * @param {Object} profile - Child's ability profile
 * @returns {boolean} True if eligible
 */
function isCheckInCheckOutEligible(profile) {
  return profile.canIndicateFeelings;
}

/**
 * SEMH Strategy Catalog
 * Each strategy includes eligibility rules and fallback strategies
 */
export const SEMH_STRATEGIES = {
  // Always eligible strategies (with adaptation)
  'visual-schedules': {
    slug: 'visual-schedules',
    title: 'Visual Schedules / Now & Next',
    category: 'ongoing',
    defaultFrequency: 0,
    defaultDurationMins: 0,
    isEligible: () => true,
    fallbacks: [],
    rationale: 'Provides structure and predictability for all children'
  },
  
  'low-arousal-response': {
    slug: 'low-arousal-response',
    title: 'Low-Arousal / De-escalation',
    category: 'ongoing',
    defaultFrequency: 0,
    defaultDurationMins: 0,
    isEligible: () => true,
    fallbacks: [],
    rationale: 'Safe, calm approach to managing challenging behavior'
  },
  
  'sensory-regulation-breaks': {
    slug: 'sensory-regulation-breaks',
    title: 'Sensory Regulation Breaks / Calm Corner',
    category: 'ongoing',
    defaultFrequency: 0,
    defaultDurationMins: 0,
    isEligible: () => true,
    fallbacks: [],
    rationale: 'Supports sensory needs and emotional regulation'
  },
  
  'intensive-interaction': {
    slug: 'intensive-interaction',
    title: 'Intensive Interaction',
    category: 'structured',
    defaultFrequency: 3,
    defaultDurationMins: 15,
    isEligible: () => true,
    fallbacks: [],
    rationale: 'Builds communication and social interaction skills'
  },
  
  'cause-and-effect': {
    slug: 'cause-and-effect',
    title: 'Cause & Effect',
    category: 'structured',
    defaultFrequency: 3,
    defaultDurationMins: 15,
    isEligible: () => true,
    fallbacks: [],
    rationale: 'Develops understanding of action-consequence relationships'
  },
  
  'movement-breaks': {
    slug: 'movement-breaks',
    title: 'Movement Breaks',
    category: 'ongoing',
    defaultFrequency: 0,
    defaultDurationMins: 0,
    isEligible: () => true,
    fallbacks: [],
    rationale: 'Supports physical regulation and attention'
  },
  
  'free-flow-continuous-provision': {
    slug: 'free-flow-continuous-provision',
    title: 'Free Flow/Outside Time',
    category: 'structured',
    defaultFrequency: 5,
    defaultDurationMins: 30,
    isEligible: () => true,
    fallbacks: [],
    rationale: 'Provides child-led learning and natural movement opportunities'
  },
  
  'functional-communication-training': {
    slug: 'functional-communication-training',
    title: 'FCT (Functional Communication Training)',
    category: 'ongoing',
    defaultFrequency: 0,
    defaultDurationMins: 0,
    isEligible: () => true,
    fallbacks: [],
    rationale: 'Teaches functional communication using appropriate modality'
  },
  
  // Ability-gated strategies
  'break-card-system': {
    slug: 'break-card-system',
    title: 'Break Card / Request a Break',
    category: 'ongoing',
    defaultFrequency: 0,
    defaultDurationMins: 0,
    isEligible: isBreakCardEligible,
    fallbacks: ['functional-communication-training', 'low-arousal-response', 'sensory-regulation-breaks', 'visual-schedules'],
    rationale: 'Teaches child to request breaks using appropriate communication method'
  },
  
  'restorative-conversation': {
    slug: 'restorative-conversation',
    title: 'Restorative Conversation (post-incident)',
    category: 'structured',
    defaultFrequency: 2,
    defaultDurationMins: 15,
    isEligible: isRestorativeConversationEligible,
    fallbacks: ['emotion-coaching', 'social-stories'],
    rationale: 'Helps child reflect on incidents and develop alternative responses'
  },
  
  'token-reward-systems': {
    slug: 'token-reward-systems',
    title: 'Token/Reward Systems',
    category: 'ongoing',
    defaultFrequency: 0,
    defaultDurationMins: 0,
    isEligible: isTokenSystemsEligible,
    fallbacks: ['first-then-visuals', 'immediate-praise', 'activity-based-reinforcement'],
    rationale: 'Motivates positive behavior through structured reward system'
  },
  
  'mindfulness-breathing': {
    slug: 'mindfulness-breathing',
    title: 'Mindfulness/Breathing',
    category: 'structured',
    defaultFrequency: 3,
    defaultDurationMins: 10,
    isEligible: isMindfulnessEligible,
    fallbacks: ['co-regulation', 'movement-breaks'],
    rationale: 'Teaches self-regulation through breathing and mindfulness techniques'
  },
  
  'social-skills-games': {
    slug: 'social-skills-games',
    title: 'Social Skills Games',
    category: 'structured',
    defaultFrequency: 2,
    defaultDurationMins: 20,
    isEligible: isSocialSkillsGamesEligible,
    fallbacks: ['parallel-play', 'intensive-interaction', 'turn-taking-1to1'],
    rationale: 'Develops social interaction skills through structured games'
  },
  
  'check-in-check-out': {
    slug: 'check-in-check-out',
    title: 'Check-in/Check-out',
    category: 'ongoing',
    defaultFrequency: 0,
    defaultDurationMins: 0,
    isEligible: isCheckInCheckOutEligible,
    fallbacks: ['adult-observation', 'zones-modelling', 'emotion-coaching-visuals'],
    rationale: 'Provides regular emotional check-ins and support'
  },
  
  // Fallback strategies (always eligible)
  'emotion-coaching': {
    slug: 'emotion-coaching',
    title: 'Emotion Coaching (adult-led, simple language)',
    category: 'ongoing',
    defaultFrequency: 0,
    defaultDurationMins: 0,
    isEligible: () => true,
    fallbacks: [],
    rationale: 'Adult-led emotional support using simple, accessible language'
  },
  
  'social-stories': {
    slug: 'social-stories',
    title: 'Social Story (rehearse alternative)',
    category: 'structured',
    defaultFrequency: 2,
    defaultDurationMins: 15,
    isEligible: () => true,
    fallbacks: [],
    rationale: 'Teaches appropriate responses through visual stories'
  },
  
  'first-then-visuals': {
    slug: 'first-then-visuals',
    title: 'First/Then with visuals',
    category: 'ongoing',
    defaultFrequency: 0,
    defaultDurationMins: 0,
    isEligible: () => true,
    fallbacks: [],
    rationale: 'Provides clear visual structure for task completion'
  },
  
  'immediate-praise': {
    slug: 'immediate-praise',
    title: 'Immediate praise + tangible reinforcer',
    category: 'ongoing',
    defaultFrequency: 0,
    defaultDurationMins: 0,
    isEligible: () => true,
    fallbacks: [],
    rationale: 'Reinforces positive behavior immediately with appropriate rewards'
  },
  
  'activity-based-reinforcement': {
    slug: 'activity-based-reinforcement',
    title: 'Activity-based reinforcement',
    category: 'ongoing',
    defaultFrequency: 0,
    defaultDurationMins: 0,
    isEligible: () => true,
    fallbacks: [],
    rationale: 'Uses preferred activities as motivation for task completion'
  },
  
  'co-regulation': {
    slug: 'co-regulation',
    title: 'Co-regulation (adult models breathing, child joins if able)',
    category: 'ongoing',
    defaultFrequency: 0,
    defaultDurationMins: 0,
    isEligible: () => true,
    fallbacks: [],
    rationale: 'Adult models regulation strategies for child to observe and potentially join'
  },
  
  'parallel-play': {
    slug: 'parallel-play',
    title: 'Parallel play + Intensive Interaction',
    category: 'structured',
    defaultFrequency: 3,
    defaultDurationMins: 20,
    isEligible: () => true,
    fallbacks: [],
    rationale: 'Supports social development through parallel activities'
  },
  
  'turn-taking-1to1': {
    slug: 'turn-taking-1to1',
    title: 'Turn-taking with adult 1:1',
    category: 'structured',
    defaultFrequency: 3,
    defaultDurationMins: 15,
    isEligible: () => true,
    fallbacks: [],
    rationale: 'Develops turn-taking skills in safe 1:1 environment'
  },
  
  'adult-observation': {
    slug: 'adult-observation',
    title: 'Adult observation + Zones modelling',
    category: 'ongoing',
    defaultFrequency: 0,
    defaultDurationMins: 0,
    isEligible: () => true,
    fallbacks: [],
    rationale: 'Adult monitors and models emotional regulation strategies'
  },
  
  'zones-modelling': {
    slug: 'zones-modelling',
    title: 'Zones modelling',
    category: 'ongoing',
    defaultFrequency: 0,
    defaultDurationMins: 0,
    isEligible: () => true,
    fallbacks: [],
    rationale: 'Teaches emotional states through visual modelling'
  },
  
  'emotion-coaching-visuals': {
    slug: 'emotion-coaching-visuals',
    title: 'Emotion Coaching with visuals only',
    category: 'ongoing',
    defaultFrequency: 0,
    defaultDurationMins: 0,
    isEligible: () => true,
    fallbacks: [],
    rationale: 'Emotional support using visual aids and minimal language'
  }
};

/**
 * Get all strategy slugs
 * @returns {string[]} Array of strategy slugs
 */
export function getAllStrategySlugs() {
  return Object.keys(SEMH_STRATEGIES);
}

/**
 * Get strategy specification by slug
 * @param {string} slug - Strategy slug
 * @returns {StrategySpec|null} Strategy specification or null if not found
 */
export function getStrategySpec(slug) {
  return SEMH_STRATEGIES[slug] || null;
}

/**
 * Get strategies by category
 * @param {'ongoing'|'structured'} category - Strategy category
 * @returns {StrategySpec[]} Array of strategy specifications
 */
export function getStrategiesByCategory(category) {
  return Object.values(SEMH_STRATEGIES).filter(strategy => strategy.category === category);
}
